import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { aiApi } from '../../../api/ai.js';
import { ApiError } from '../../../api/client.js';
import { Card } from '../../../components/ui/Card.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Skeleton } from '../../../components/ui/Skeleton.jsx';
import { t } from '../../../lib/i18n.js';

export function PersonalityAssignments({ canManage }) {
  const queryClient = useQueryClient();

  const assignQuery = useQuery({ queryKey: ['ai', 'personality', 'assignments'], queryFn: aiApi.getPersonalityAssignments });
  const listQuery = useQuery({ queryKey: ['ai', 'personalities', 'all'], queryFn: () => aiApi.listPersonalities({ limit: 100 }) });

  const agents = assignQuery.data?.agents || [];
  const personalities = listQuery.data?.items || [];
  const activeName = personalities.find((p) => p.isActive)?.name;

  const [map, setMap] = useState({});
  useEffect(() => { if (assignQuery.data) setMap(assignQuery.data.assignments || {}); }, [assignQuery.data]);

  const save = useMutation({
    mutationFn: () => aiApi.setPersonalityAssignments(map),
    onSuccess: () => { toast.success('Assegnazioni salvate.'); queryClient.invalidateQueries({ queryKey: ['ai', 'personality', 'assignments'] }); },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const setAgent = (agentId, pid) => setMap((m) => {
    const next = { ...m };
    if (pid) next[agentId] = pid;
    else delete next[agentId];
    return next;
  });

  const loading = assignQuery.isLoading || listQuery.isLoading;

  return (
    <Card>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-ink">Personalità per compito</h2>
          <p className="text-xs text-ink-muted">
            Scegli quale personalità usa ogni agente dell'assistente. "Predefinita" usa quella attiva{activeName ? ` (${activeName})` : ''}.
          </p>
        </div>
        {canManage && <Button onClick={() => save.mutate()} loading={save.isPending}>Salva assegnazioni</Button>}
      </header>

      {loading ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {agents.map((a) => (
            <div key={a.id} className="flex flex-col gap-1.5 rounded-lg border border-border bg-surface p-3">
              <span className="text-sm font-medium text-ink">{a.label}</span>
              <Select value={map[a.id] || ''} disabled={!canManage} onChange={(e) => setAgent(a.id, e.target.value)}>
                <option value="">Predefinita (personalità attiva)</option>
                {personalities.map((p) => (
                  <option key={p._id} value={p._id}>{p.name}{p.isActive ? ' · attiva' : ''}</option>
                ))}
              </Select>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
